/**
 * Minimal focus trap for modal-ish UI surfaces.
 *
 * When `activate(container)` is called, Tab and Shift+Tab cycle focus
 * only between the focusable elements inside `container`. Escape calls
 * the provided `onEscape` callback. `deactivate()` restores focus to
 * the previously-active element and removes the keydown listener.
 *
 * The settings drawer and the post-run submit form both render
 * dialog-shaped content over the game canvas; without this, Tab from
 * inside them lands on canvas-adjacent elements that have no visual
 * focus indicator, leaving keyboard users stranded.
 */

const FOCUSABLE_SELECTORS = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled]):not([type='hidden'])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  "[tabindex]:not([tabindex='-1'])",
].join(",");

export class FocusTrap {
  private container: HTMLElement | null = null;
  private previouslyFocused: HTMLElement | null = null;
  private onEscape: (() => void) | null = null;

  activate(container: HTMLElement, onEscape?: () => void) {
    if (this.container) this.deactivate();
    this.container = container;
    this.previouslyFocused = document.activeElement as HTMLElement | null;
    this.onEscape = onEscape ?? null;

    // Move focus into the trap. Skip on coarse pointers where pulling up
    // the on-screen keyboard hides game state.
    const isCoarse = matchMedia("(pointer: coarse)").matches;
    if (!isCoarse) {
      const first = this.focusables()[0];
      first?.focus({ preventScroll: true });
    }

    document.addEventListener("keydown", this.onKeydown);
  }

  deactivate() {
    if (!this.container) return;
    document.removeEventListener("keydown", this.onKeydown);
    this.container = null;
    this.onEscape = null;
    this.previouslyFocused?.focus?.({ preventScroll: true });
    this.previouslyFocused = null;
  }

  private focusables(): HTMLElement[] {
    if (!this.container) return [];
    return Array.from(
      this.container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTORS),
    ).filter((el) => !el.hidden && el.offsetParent !== null);
  }

  private onKeydown = (e: KeyboardEvent) => {
    if (!this.container) return;

    if (e.key === "Escape" && this.onEscape) {
      e.preventDefault();
      this.onEscape();
      return;
    }

    if (e.key !== "Tab") return;
    const items = this.focusables();
    if (items.length === 0) {
      e.preventDefault();
      return;
    }
    const first = items[0];
    const last = items[items.length - 1];
    const active = document.activeElement as HTMLElement;

    if (e.shiftKey && active === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && active === last) {
      e.preventDefault();
      first.focus();
    }
    // else: native Tab cycles between items inside the trap as normal
  };
}
